/**
 * MainNavigation.tsx
 * The tab row: Accounts, Locations, My Work, Reports, Help and Add Work
 * Order. It never picks an account or location — each page shows its own
 * place (Accounts lists locations and its name switches account, a
 * location's name switches location, Reports has its own filter).
 */
import React from 'react';
import { User } from '../../services/auth';
import { TrendingUp, FileBarChart, Plus, Users, HelpCircle, ClipboardCheck, Contact } from 'lucide-react';
import { theme } from '@turn-connect/ui';
import { useResponsive, isSystemAdmin as isSystemAdminCheck } from '@turn-connect/hooks';
import { navigateToWorkOrders } from '../../utils/navigation';
import { canShowExecutiveDashboard, getDashboardGates } from '../../utils/dashboardAccess';
import { HeaderContainer } from '../layouts/HeaderContainer';
import { useMyWork } from '../../hooks/useMyWork';
import { useCustomersTab } from '../../features/customers/useCustomersTab';

type NavView = 'dashboard' | 'dashboard:accounts' | 'dashboard:location' | 'workorders' | 'customers' | 'mywork' | 'reports' | 'help';

interface MainNavigationProps {
  currentView: string;
  onNavigate: (view: NavView) => void;
  /**
   * Opens the app-level create-work-order modal over the CURRENT view.
   * When omitted, falls back to navigating to the work-order list with
   * `create=true` (the legacy double-transition flow).
   */
  onCreateWorkOrder?: () => void;
  accountLogo?: React.ReactNode;
  user: User;
  permissions: string[];
  /** The account and location in scope: the legacy Add Work Order fallback creates there. */
  selectedAccountId?: string | null;
  selectedLocationId?: string | null;
  /** The person can open at least one location (shows Locations). */
  hasLocations?: boolean;
  /**
   * There is another location to go to. Locations then shows even where the
   * person's role lacks the location dashboard: its page is where they pick
   * another location, now that the top bar has no picker.
   */
  canSwitchLocation?: boolean;
}

const MainNavigation: React.FC<MainNavigationProps> = ({
  currentView,
  onNavigate,
  onCreateWorkOrder,
  accountLogo,
  user,
  permissions,
  selectedAccountId,
  selectedLocationId,
  hasLocations = false,
  canSwitchLocation = false,
}) => {
  // Actionable-count badge for My Work — self-contained so no page threads
  // a prop through MainLayout. SSE + focus/poll freshness lives in the hook.
  const { count: myWorkCount } = useMyWork();
  // Customers module: its tab only while the account has it on and the person can see customers.
  const showCustomers = useCustomersTab(selectedAccountId, permissions);
  // Responsive hooks
  const { isMobile, width } = useResponsive();
  // With no account or location boxes on this row, the five tabs keep their
  // labels from an iPad in portrait (768px) up. Five labelled tabs and the
  // labelled Add Work Order button need about 800px (measured), so below
  // 900px the button shows its + only (named and with a tooltip), and the
  // tabs never run under it.
  // A sixth tab (Customers) needs about 110px more.
  const iconOnlyAdd = isMobile || width < (showCustomers ? 1010 : 900);

  const ICON_SIZE = isMobile ? 20 : 18;

  // Determine user type for navigation visibility
  const isSystemAdmin = isSystemAdminCheck(permissions) || user?.role === 'super_admin';
  const isAccountAdmin = permissions.includes('account:admin') || user?.role === 'account_admin';

  // Check if user has an account-wide role assignment (for showing Executive dashboard)
  // Account-wide roles are stored in user.accounts[].roles, not user.locations[].roles
  const userAccounts = (user as any)?.accounts || [];
  const hasAccountScopeRole = userAccounts.some((acc: any) => (acc?.roles || []).some((r: any) => r.is_active !== false));

  // Users with account-scope roles OR account:admin permission should see account-level features
  const canAccessAccountDashboard = isSystemAdmin || isAccountAdmin || hasAccountScopeRole;

  // Check if user has access to any location (for showing Departments/Location Dashboard button)
  // Users with location access can view the location dashboard even if they're not admins
  const hasLocationAccess = !!(selectedLocationId || (user as any)?.location_id || hasLocations);

  // Dashboard visibility gates — shared single source of truth (see dashboardAccess).
  // Resolved from the current location-scoped `permissions`, so the tabs adapt
  // when the user switches location.
  const { canViewLocationDashboard } = getDashboardGates(user, permissions);
  // Executive dashboard tab mirrors the backend permission gate exactly — see
  // canShowExecutiveDashboard (per-user overrides grant the permission without
  // an account-scope role).
  const showExecutiveDashboard = canShowExecutiveDashboard(user, permissions);

  // Check if user can create work orders
  const canCreateWorkOrder = permissions.includes('workorders:create') || isSystemAdmin || isAccountAdmin;

  // Determine if a view is active
  const isActive = (view: NavView) => {
    if (currentView === view) return true;

    // Handle dashboard variants
    if (view === 'dashboard:accounts' && currentView === 'dashboard' && showExecutiveDashboard) {
      return true;
    }
    if (view === 'dashboard:location' && currentView === 'dashboard' && !showExecutiveDashboard) {
      return true;
    }

    return false;
  };

  // Handle create work order — prefer the app-level in-place modal so the
  // current view (e.g. the location dashboard) stays visible underneath.
  // With the app-level form the button is always live: the form starts at
  // the location on screen, or asks which one. The legacy list fallback still
  // needs a selected account and location.
  const createReady = !!onCreateWorkOrder || (!!selectedLocationId && !!selectedAccountId);
  const handleCreateWorkOrder = () => {
    if (!canCreateWorkOrder || !createReady) {
      return;
    }

    if (onCreateWorkOrder) {
      onCreateWorkOrder();
      return;
    }
    if (!selectedLocationId || !selectedAccountId) return;

    // Legacy fallback: navigate to work orders view with create modal open
    navigateToWorkOrders(selectedAccountId, selectedLocationId, { create: true });
  };

  return (
    <nav
      style={{
        position: 'sticky',
        top: isMobile ? '3.5rem' : '3.75rem', // Below GlobalHeader (matches header minHeight)
        zIndex: 999,
        backgroundColor: theme.colors.background.secondary,
        borderBottom: `1px solid ${theme.colors.border.default}`,
      }}
    >
      <HeaderContainer
        style={{
          minHeight: isMobile ? '3rem' : '3.5rem',
          paddingTop: isMobile ? '0.375rem' : '0.5rem',
          paddingBottom: isMobile ? '0.375rem' : '0.5rem',
          gap: isMobile ? '0.5rem' : '1rem',
          // Never wrap: icon-only compact tabs absorb narrow widths, so the
          // Add Work Order button stays on the bar instead of dropping to a
          // second line.
          flexWrap: 'nowrap',
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Navigation Tabs */}
        <div
          style={{
            flex: isMobile ? '1 0 auto' : 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.125rem' : '0.25rem',
            overflow: isMobile ? 'auto' : 'visible',
          }}
        >
          {/* Accounts (the executive dashboard) - account-scope users who hold dashboards:executive:view */}
          {showExecutiveDashboard && (
            <NavTab
              icon={<TrendingUp size={ICON_SIZE} />}
              label={isMobile ? '' : 'Accounts'}
              title="Accounts"
              isActive={isActive('dashboard:accounts')}
              onClick={() => onNavigate('dashboard:accounts')}
              isMobile={isMobile}
            />
          )}

          {/* Locations (the location dashboard) - users with location access who hold dashboards:location:view,
              or who have another location to switch to from it */}
          {(canAccessAccountDashboard || hasLocationAccess) && (canViewLocationDashboard || canSwitchLocation) && (
            <NavTab
              icon={<Users size={ICON_SIZE} />}
              label={isMobile ? '' : 'Locations'}
              title="Locations"
              isActive={isActive('dashboard:location')}
              onClick={() => onNavigate('dashboard:location')}
              isMobile={isMobile}
            />
          )}

          {/* Customers (optional module) */}
          {showCustomers && (
            <NavTab
              icon={<Contact size={ICON_SIZE} />}
              label={isMobile ? '' : 'Customers'}
              title="Customers"
              isActive={isActive('customers')}
              onClick={() => onNavigate('customers')}
              isMobile={isMobile}
              testId="customers-tab"
            />
          )}

          {/* My Work — personal actionable hub; badge = things waiting on you */}
          <NavTab
            icon={<ClipboardCheck size={ICON_SIZE} />}
            label={isMobile ? '' : 'My Work'}
            title="My Work"
            isActive={isActive('mywork')}
            onClick={() => onNavigate('mywork')}
            isMobile={isMobile}
            badge={myWorkCount}
          />

          {/* Reports */}
          <NavTab
            icon={<FileBarChart size={ICON_SIZE} />}
            label={isMobile ? '' : 'Reports'}
            title="Reports"
            isActive={isActive('reports')}
            onClick={() => onNavigate('reports')}
            isMobile={isMobile}
          />

          {/* Help Center — available to every signed-in user */}
          <NavTab
            icon={<HelpCircle size={ICON_SIZE} />}
            label={isMobile ? '' : 'Help'}
            title="Help"
            isActive={isActive('help')}
            onClick={() => onNavigate('help')}
            isMobile={isMobile}
          />
        </div>

        {/* Add Work Order Button */}
        {canCreateWorkOrder && (
          <button
            onClick={handleCreateWorkOrder}
            disabled={!createReady}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: iconOnlyAdd ? 0 : '0.375rem',
              padding: isMobile ? '0.625rem' : iconOnlyAdd ? '0.5rem 0.625rem' : '0.5rem 1rem',
              backgroundColor: createReady ? theme.colors.brand.fill : 'var(--tc-background-elevated, #333333)',
              border: 'none',
              borderRadius: '0.5rem',
              color: createReady ? theme.colors.text.onAccent : 'var(--tc-text-quaternary, #666666)',
              cursor: createReady ? 'pointer' : 'not-allowed',
              fontSize: '0.8125rem',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              opacity: createReady ? 1 : 0.5,
              minWidth: isMobile ? '2.75rem' : 'auto',
              minHeight: isMobile ? '2.75rem' : 'auto',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (createReady) {
                e.currentTarget.style.backgroundColor = theme.colors.brand.fillHover;
              }
            }}
            onMouseLeave={(e) => {
              if (createReady) {
                e.currentTarget.style.backgroundColor = theme.colors.brand.fill;
              }
            }}
            title={createReady ? 'Add Work Order' : 'Select an account and location to create work order'}
            aria-label="Add Work Order"
          >
            <Plus size={isMobile ? 22 : 18} />
            {!iconOnlyAdd && <span>Add Work Order</span>}
          </button>
        )}
      </HeaderContainer>
    </nav>
  );
};

interface NavTabProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  isMobile?: boolean;
  /** Tooltip shown when the tab renders icon-only (compact/mobile) */
  title?: string;
  /** Red actionable-count chip; 0/undefined renders nothing. */
  badge?: number;
  testId?: string;
}

const NavTab: React.FC<NavTabProps> = ({ icon, label, isActive, onClick, isMobile = false, title, badge, testId }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={label ? undefined : title}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? 0 : '0.5rem',
        padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 1rem',
        backgroundColor: isActive
          ? 'var(--tc-overlay-washStrong, rgba(255, 255, 255, 0.08))'
          : isHovered
            ? 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))'
            : 'transparent',
        border: 'none',
        borderRadius: '0.5rem',
        color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
        cursor: 'pointer',
        fontSize: '0.8125rem',
        fontWeight: isActive ? 600 : 500,
        transition: 'all 0.2s ease',
        position: 'relative',
        minWidth: isMobile ? '2.75rem' : 'auto',
        minHeight: isMobile ? '2.75rem' : 'auto',
        flexShrink: 0,
      }}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            bottom: -1,
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobile ? '60%' : '80%',
            height: '0.125rem',
            backgroundColor: 'var(--tc-brand-primary, #DA562C)',
            borderRadius: '0.125rem 0.125rem 0 0',
          }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
      {label && <span>{label}</span>}
      {!!badge && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            background: 'var(--tc-status-error, #DC2626)',
            color: 'var(--tc-text-onAccent, #FFFFFF)',
            borderRadius: 9,
            minWidth: 16,
            padding: '1px 4px',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '14px',
            textAlign: 'center',
          }}
          data-testid="mywork-nav-badge"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
};

export default MainNavigation;
